import {
  AddCompanyActivityTagRequest,
  CreateCompanyActivityRequest,
  UpdateCompanyActivityRequest,
  UpdateCompanyCompletionCriterionRequest,
  validateAddApproverToCompanyActivity,
  validateAddDocumentToCompanyActivity,
  validateAddLinkToCompanyActivity,
  validateAddTagToCompanyActivity,
  validateAddWatcherToCompanyActivity,
  validateCreateCompanyActivityRequest,
  validateRemoveApproverFromCompanyActivity,
  validateRemoveCompanyActivityRequest,
  validateRemoveDocumentFromCompanyActivity,
  validateRemoveLinkFromCompanyActivity,
  validateRemoveTagFromCompanyActivity,
  validateRemoveWatcherFromCompanyActivity,
  validateUpdateCompanyActivityRequest,
  validateUpdateCompanyCCRequest,
  validateIfUniqueCompanyActivityLabel,
} from '@beeboard/common';
import { defineStore } from 'pinia';
import { useCompanyActivityStore } from './companyActivityStore';

export const useCompanyActivityRulesStore = defineStore(
  'companyActivityRulesStore',
  {
    actions: {
      validateCreateCompanyActivityRequest(
        payload: CreateCompanyActivityRequest,
      ) {
        return validateCreateCompanyActivityRequest(payload);
      },

      validateUpdateCompanyActivityRequest(
        activityId: string,
        payload: UpdateCompanyActivityRequest,
      ) {
        const store = useCompanyActivityStore();

        return validateUpdateCompanyActivityRequest(
          payload,
          store.companyActivity(activityId),
        );
      },

      validateRemoveCompanyActivityRequest(activityId: string) {
        const store = useCompanyActivityStore();
        const activity = store.hasCompanyActivity(activityId)
          ? store.companyActivity(activityId)
          : store.companyActivityDetails(activityId);

        return validateRemoveCompanyActivityRequest(activity);
      },

      validateUpdateCompanyCCRequest(
        payload: UpdateCompanyCompletionCriterionRequest,
      ) {
        return validateUpdateCompanyCCRequest(payload);
      },

      validateAddDocumentToCompanyActivity(
        activityId: string,
        documentId: string,
      ) {
        const activity = useCompanyActivityStore().companyActivity(activityId);

        return validateAddDocumentToCompanyActivity(
          activity.documents.map((d) => d.id),
          documentId,
        );
      },

      validateRemoveDocumentFromCompanyActivity(
        activityId: string,
        documentId: string,
      ) {
        const activity = useCompanyActivityStore().companyActivity(activityId);

        return validateRemoveDocumentFromCompanyActivity(
          activity.documents.map((d) => d.id),
          documentId,
        );
      },

      validateAddLinkToCompanyActivity(activityId: string, linkId: string) {
        const activity = useCompanyActivityStore().companyActivity(activityId);

        return validateAddLinkToCompanyActivity(
          activity.links.map((d) => d.id),
          linkId,
        );
      },

      validateRemoveLinkFromCompanyActivity(
        activityId: string,
        linkId: string,
      ) {
        const activity = useCompanyActivityStore().companyActivity(activityId);

        return validateRemoveLinkFromCompanyActivity(
          activity.links.map((d) => d.id),
          linkId,
        );
      },

      validateAddApproverToCompanyActivity(
        activityId: string,
        approverId: string,
      ) {
        const activity = useCompanyActivityStore().companyActivity(activityId);

        return validateAddApproverToCompanyActivity(
          activity.responsible,
          activity.approvers,
          activity.watchers,
          approverId,
        );
      },

      validateRemoveApproverFromCompanyActivity(
        activityId: string,
        approverId: string,
      ) {
        const activity = useCompanyActivityStore().companyActivity(activityId);

        return validateRemoveApproverFromCompanyActivity(
          activity.approvers,
          approverId,
        );
      },

      validateAddWatcherToCompanyActivity(
        activityId: string,
        watcherId: string,
      ) {
        const activity = useCompanyActivityStore().companyActivity(activityId);

        return validateAddWatcherToCompanyActivity(
          activity.responsible,
          activity.approvers,
          activity.watchers,
          watcherId,
        );
      },

      validateRemoveWatcherFromCompanyActivity(
        activityId: string,
        watcherId: string,
      ) {
        const activity = useCompanyActivityStore().companyActivity(activityId);

        return validateRemoveWatcherFromCompanyActivity(
          activity.watchers,
          watcherId,
        );
      },

      validateAddTagToCompanyActivity(
        activityId: string,
        payload: AddCompanyActivityTagRequest,
      ) {
        const activity = useCompanyActivityStore().companyActivity(activityId);

        return validateAddTagToCompanyActivity(payload, activity.tags);
      },

      validateRemoveTagFromCompanyActivity(activityId: string, tagId: string) {
        const activity = useCompanyActivityStore().companyActivity(activityId);

        return validateRemoveTagFromCompanyActivity(tagId, activity.tags);
      },
      validateIfUniqueCompanyActivityLabel(
        newTitle: string,
        labelTitles: string[],
      ) {
        return validateIfUniqueCompanyActivityLabel(newTitle, labelTitles);
      },
    },
  },
);
